'use client';

import { Button, Modal } from '@common/components';
import { useToast } from '@common/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AuthMutation } from '@/api';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { converter } from '../_utils/converter';

interface DonationReservationModalProps {
  opened: boolean;
  businessId?: number;
  productNumber?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  description?: string;
}

/**
 * * 기부 사전예약 임시 모달
 */
export const DonationReservationModal = ({
  opened,
  businessId,
  productNumber,
  onSuccess,
  onClose,
  description,
}: DonationReservationModalProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { toastError } = useToast();
  const queryClient = useQueryClient();

  const { mutate: createReservation, isPending: isCreatingReservation } =
    useMutation({
      ...AuthMutation['post:/wello/v2/auth/home/donation/reservation']({
        body: {
          homeDonationBusinessId: businessId,
          productNumber: productNumber,
        },
      }),
      onSuccess: () => {
        onSuccess?.();
        queryClient.invalidateQueries();
      },
      onError: (error) => {
        toastError({ error });
      },
    });

  const safeHighlightedDescription = converter.highlight(
    converter.sanitizeHTML(description ?? ''),
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      className="max-h-[90%] min-w-240 p-0 lg:max-h-[90%] lg:min-w-240 lg:p-0"
      loading={isCreatingReservation}
    >
      <div className="flex flex-col items-center px-20 pb-24 pt-32">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
        >
          <path
            opacity="0.4"
            d="M40 16V24.92C40 25.33 39.604 25.602 39.21 25.48C38.04 25.116 36.792 24.96 35.478 25.01C29.908 25.26 25.26 29.91 25.01 35.48C24.95 36.8 25.11 38.04 25.48 39.21C25.6 39.61 25.33 40.0 24.918 40.0H10C6 40.0 4 38.0 4 34.0V16.0H40V16Z"
            fill="#33A6FF"
          />
          <path
            d="M34 7H31.5V4C31.5 3.172 30.828 2.5 30 2.5C29.172 2.5 28.5 3.172 28.5 4V7H15.5V4C15.5 3.172 14.828 2.5 14 2.5C13.172 2.5 12.5 3.172 12.5 4V7H10C6 7 4 9 4 13V16H40V13C40 9 38 7 34 7Z"
            fill="#33A6FF"
          />
          <path
            d="M14.04 26C12.94 26 12.03 25.104 12.03 24C12.03 22.896 12.92 22 14.02 22H14.04C15.15 22 16.04 22.896 16.04 24C16.04 25.104 15.14 26 14.04 26ZM24.04 24C24.04 22.896 23.15 22 22.04 22H22.02C20.92 22 20.03 22.896 20.03 24C20.03 25.104 20.94 26 22.04 26C23.14 26 24.04 25.104 24.04 24ZM16.04 32C16.04 30.896 15.15 30 14.04 30H14.02C12.92 30 12.03 30.896 12.03 32C12.03 33.104 12.94 34 14.04 34C15.14 34 16.04 33.104 16.04 32Z"
            fill="#33A6FF"
          />
          <path
            d="M36 28C31.582 28 28 31.582 28 36C28 40.418 31.582 44 36 44C40.418 44 44 40.418 44 36C44 31.582 40.418 28 36 28ZM38.85 38.85C38.65 39.04 38.4 39.14 38.14 39.14C37.89 39.14 37.63 39.04 37.43 38.85L35.292 36.71C35.104 36.52 35 36.264 35 35.998V32.998C35 32.446 35.448 31.998 36 31.998C36.552 31.998 37 32.446 37 32.998V35.584L38.85 37.43C39.24 37.83 39.24 38.46 38.85 38.85Z"
            fill="#33A6FF"
          />
        </svg>

        <p className="py-8 font-700 text-Gray-900 typo-body2">
          기부 사전 예약 안내
        </p>

        <p
          className="pb-8 font-400 text-Gray-700 typo-body3"
          dangerouslySetInnerHTML={{
            __html: safeHighlightedDescription,
          }}
        />

        <br />

        <p className="pb-8 font-400 text-Gray-700 typo-caption1">
          사전 예약을 해주시면,
          <br />
          기부 서비스 재개 시 알림톡을 발송 드립니다.
          <br />
          이벤트 및 세액 공제 혜택은
          <br />
          기부 시점을 기준으로 적용됩니다.
        </p>

        <Button
          theme="primary-fill"
          dimensions={isDesktopViewport ? 'H56' : 'H48'}
          className="mt-16 w-full"
          onClick={() =>
            createReservation({
              body: {
                homeDonationBusinessId: businessId,
                productNumber: productNumber,
              },
            })
          }
          loading={isCreatingReservation}
        >
          사전 예약 신청하기
        </Button>
      </div>
    </Modal>
  );
};
